import { useEffect, useMemo, useState } from 'react';
import { usePrevious, useQueryFetch } from 'sonata-react-components';

import { preprocessCsvFile } from '@/services/file.service';
import { useMultiFlightMap } from '../../../MultiFlightMap.context';
import { uploadEntityFile } from '../../../multiFlightMap.resources';
import { LocationCSVUploadModalContext } from './LocationCSVUploadModalContext';

import type { CsvImporterConfig, CsvMapping } from '@/components/common/CsvImporter/csvImporter.helpers';
import type { LocationCSVUploadState } from '../types';

export type LocationCSVUploadModalProps = {
  children: JSX.Element | JSX.Element[];
  initialCountry: string;
};

export function LocationCSVUploadModalProvider({ children, initialCountry }: Readonly<LocationCSVUploadModalProps>) {
  const [{ config, country, file, mapping }, setState] = useState<LocationCSVUploadState>({
    config: {
      delimiter: 'comma',
      separator: 'comma'
    },
    country: initialCountry,
    mapping: []
  });

  const { entityId, entityType } = useMultiFlightMap();
  const requestToken: Record<string, string> = {};

  const setConfig = (nextConfig: CsvImporterConfig) =>
    setState(prev => ({
      ...prev,
      config: nextConfig,
      mapping: nextConfig.delimiter === prev.config.delimiter ? prev.mapping : []
    }));
  const setCountry = (nextCountry: string) => setState(prev => ({ ...prev, country: nextCountry }));
  const setFile = (nextFile?: File) => setState(prev => ({ ...prev, file: nextFile, mapping: [] }));
  const setMapping = (nextMapping: CsvMapping[]) => setState(prev => ({ ...prev, mapping: nextMapping }));

  const {
    data: preprocess,
    refetch,
    status: preprocessStatus
  } = useQueryFetch(
    ['location-csv-upload-preprocess', config.delimiter, config.separator, file?.name, file?.lastModified],
    () =>
      preprocessCsvFile({
        delimiter: config.delimiter,
        file: file!,
        numberOfLines: 50,
        numericSeparator: config.separator
      }),
    {
      enabled: !!file
    }
  );

  const uploadFileCondition = !!(preprocess && preprocess?.errors.length === 0 && !!file);
  const { data: uploadedFile, status: uploadedFileStatus } = useQueryFetch(
    ['file-upload', file?.name, file?.lastModified, 'zones'],
    async () => uploadEntityFile(entityType, entityId, 'zones', file!, requestToken),
    {
      enabled: uploadFileCondition
    }
  );

  const previousFile = usePrevious(file);
  useEffect(() => {
    if (!previousFile || !file) {
      return;
    }
    if (file.name !== previousFile.name || file.lastModified !== previousFile.lastModified) {
      refetch();
      setState(prev => ({ ...prev, mapping: [] }));
    }
  }, [file, previousFile]);

  const providerData = useMemo(
    () => ({
      config,
      country,
      file,
      mapping,
      preprocess,
      preprocessStatus,
      setConfig,
      setCountry,
      setFile,
      setMapping,
      uploadedFile,
      uploadedFileStatus
    }),
    [config, country, file, mapping, preprocess, preprocessStatus, uploadedFile, uploadedFileStatus]
  );

  return (
    <LocationCSVUploadModalContext.Provider value={providerData}>{children}</LocationCSVUploadModalContext.Provider>
  );
}
